import React, {Fragment, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import {DialogContent, Divider, Grid} from '@mui/material';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import BiotechIcon from '@mui/icons-material/Biotech';
import SettingsIcon from '@mui/icons-material/Settings';
import {useDispatch, useSelector} from 'react-redux';
import {hasOneOfPermission, hasPermission, Permissions} from '../../../../store/reducers/user';
import {additionalData, updatePatientAdditionalData} from '../../../../store/reducers/additionalData';
import UrinalysisTestNew from './urinalysisTestNew';
import UrinalysisSettings from './urinalysisSettings';
import _ from 'lodash';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import config from '../../../../app/config';
import IconButton from '@mui/material/IconButton';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import MessageBox from '../../../../util/messageBox';
import API from '../../../../app/api';
import {useTheme} from '@mui/material/styles';
import InformationBox from '../../../../components/other/informationBox';
import {setSnackbar} from '../../../../store/reducers/globals';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import Typography from '@mui/material/Typography';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import useOpenState from '../../../../hooks/useOpenState';

export const useStylesUrinalysis = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
        fontWeight: 'bold',
    },
    dialogContent: {
        width: '100%',
        height: '100%',
    },
    fullWidth: {
        width: '100%',
    },
    divider: {
        padding: '10px 0px',
        width: '100%',
    },
    adderBorder: {
        border: 'solid 1px #b9b9b9',
        borderRadius: 5,
        padding: 5,
        width: '100%',
    },
    adder: {
        width: '100%',
        minHeight: 380,
    },
    footer: {
        bottom: 0,
        left: 0,
        position: 'fixed',
    },
    title: {
        textAlign: 'center',
        paddingBottom: 5,
    },
    tableRow: {
        '&:hover': {
            background: theme.palette.mode === 'dark' ? '#414141' : '#ccc',
        },
    },
    headCell: {
        fontSize: 'small',
        background: theme.palette.background.default,
    },
    rowCell: {
        fontSize: 'small',
        '&.odd': {
            background: theme.palette.mode === 'light' ? '#d1d1d1' : '#666666',
        },
    },
    colourBrick: {
        display: 'flex',
        justifyContent: 'center',
        width: 100,
        height: 10,
    },
    testBar: {
        width: 50,
        height: 50,
        borderRadius: 10,
        '&.non-pointer': {
            pointerEvents: 'none',
        },
    },
    infoBox: {
        borderRadius: '5px',
        border: '1px solid #7199E6',
        backgroundColor: theme.palette.warning.main,
        padding: 5,
        color: theme.palette.mode === 'light' ? '#fff' : '#000',
        width: '100%',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 'large',
    },
    mainTestBox: {
        width: '100%',
        marginTop: 15,
        padding: 10,
    },
    testEntry: {
        width: '100%',
        backgroundColor: theme.palette.background.default,
        padding: 5,
        marginBottom: 10,
        '&.paper-background': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    valuesBox: {
        marginLeft: 5,
    },
    values: {
        marginLeft: 15,
        textAlign: 'center',
        width: 100,
    },
    emptyInfo: {
        textAlign: 'center',
        fontSize: 'large',
        fontWeight: 'bold',
        width: '100%',
    },
    strikethrough: {
        textDecoration: 'line-through',
    },
    additionalInfo: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        margin: 8,
    },
    backgroundOverride: {
        backgroundColor: `${theme.palette.inPatient.formBackground} !important`,
    },
    actionNoDivider: {
        borderBottom: 'none',
    },
}));

const Urinalysis = (props) => {
    const {t} = useTranslation();
    const classes = useStylesUrinalysis();
    const patientAddData = useSelector(additionalData);
    const dispatch = useDispatch();
    const theme = useTheme();

    // Permissions
    const canViewUrinalysis = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_VIEW_URINALYSIS, Permissions.AGYLE_CAN_ADD_URINALYSIS, Permissions.AGYLE_CAN_EDIT_URINALYSIS]));
    const canAddUrinalysis = dispatch(hasPermission(Permissions.AGYLE_CAN_ADD_URINALYSIS));
    const canEditUrinalysis = dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_URINALYSIS));

    // List
    const [list, setList] = useState([]);
    const [currentAtt, setCurrentAtt] = useState(true);

    // Open states
    const [isNewTestOpen, openNewTest, closeNewTest] = useOpenState(!!props.ice, true);
    const [isSettingsOpen, openSettings, closeSettings] = useOpenState(false, true);
    const [isAdditionalInfoOpen, openAdditionalInfo, closeAdditionalInfo] = useOpenState(false, true);

    // Cancel entry
    const [openConfirm, setOpenConfirm] = useState(false);
    const cancelID = useRef(null);

    // Additional Info
    const message = useRef('');

    // Init data
    useEffect(() => {
        if (canViewUrinalysis && patientAddData?.other?.urinalysis) {
            let records = patientAddData.other.urinalysis;
            const startDate = moment(props.patient?.admission?.startDate);
            const endDate = props.patient?.admission?.endDate ? moment(props.patient?.admission?.endDate) : moment();

            if (currentAtt) {
                records = _.filter(records, record => {
                    if (record?.attendanceId) return record.attendanceId === props.patient?.admission?.__id;
                    return moment(record.when).isBetween(startDate, endDate);
                });
            }

            setList(_.orderBy(records, item => {
                return moment(item.when);
            }, ['desc']));
        }
    }, [patientAddData, currentAtt]);

    const cancelEntry = () => {
        if (cancelID.current) {
            setOpenConfirm(false);
            const id = encodeURIComponent(cancelID.current);
            API.cancelClinicalNoteByRefId(props.patient, id).then(r => {
                if (r) {
                    disableEntry(id);
                }
            }).catch(error => {
                console.error('cancelClinicalNoteByRefId error', error);
                dispatch(setSnackbar({type: 'error', message: `Cancel operation failed ${error.message}`}));
            });
        }
    };

    const disableEntry = (id) => {
        let clone = _.cloneDeep(list);
        let index = _.findIndex(clone, {id});
        if (index !== -1) {
            clone[index] = {
                ...clone[index],
                deleted: true,
            };

            const data = {
                'urinalysis': clone,
            };
            dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
        }

    };

    const renderBHCG = (bhcg) => {
        if (!bhcg) return '-';

        // eslint-disable-next-line default-case
        switch (bhcg) {
            case 'notTested':
                return 'N/T';
            case 'positive':
                return 'POS';
            case 'negative':
                return 'NEG';
        }
    };

    const renderLists = () => {
        if (!_.isEmpty(list)) {
            return (
                list.map(element => (
                    <Paper
                        className={clsx(
                            element.deleted && classes.strikethrough,
                            classes.testEntry,
                            'paper-background',
                        )}
                        key={element.id}
                    >
                        <Grid container direction={'row'} spacing={2}>
                            <Grid item xs={3}>
                                {element.stripTestName}
                            </Grid>

                            <Grid item xs={4}>
                                {element.by}
                            </Grid>

                            <Grid item xs={3}>
                                {element.when ? moment(element.when).format(config.defaultDateTimeFormat) : '-'}
                            </Grid>

                            <Grid item xs={1} style={{fontWeight: 'bold'}}>
                                {renderBHCG(element.bhcg)}
                            </Grid>

                            <Grid item xs={1}>
                                <IconButton
                                    onClick={() => {
                                        cancelID.current = element.id;
                                        setOpenConfirm(true);
                                    }}
                                    disabled={element.deleted || props.disableSave}
                                    size='small'
                                >
                                    <CancelPresentationRoundedIcon fontSize='small'/>
                                </IconButton>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider/>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container direction='row'>
                                    {element.skipped &&
                                        <InformationBox type='info'>
                                            {t('Urinalysis test not recorded')}
                                        </InformationBox>
                                    }

                                    {!element.skipped && element.tests.map(test => (
                                        <div className={classes.values}
                                             key={test.valueColour + test.category}
                                        >
                                            <div style={{textAlign: 'center', fontSize: 'small', fontStyle: 'italic'}}>
                                                {`${test.category} [${test.categoryShortcut}]`}
                                            </div>

                                            <IconButton
                                                disableRipple
                                                className={clsx(classes.testBar, 'non-pointer')}
                                                style={{
                                                    backgroundColor: test.valueColour,
                                                    backgroundImage: test.valueGrainType ? `url(/image/urinalysis/grain_typ_${test.valueGrainType}.png)` : '',
                                                    backgroundSize: 'contain',
                                                    border: `2px solid ${theme.palette.mode === 'light' ? '#000' : '#fff'}`,
                                                }}
                                            />

                                            <div style={{textAlign: 'center', fontSize: 'small'}}>
                                                {test.value}
                                            </div>
                                        </div>
                                    ))}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider/>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                className={classes.additionalInfo}
                                onClick={() => {
                                    if (element.additionalInformation && element.additionalInformation !== '') {
                                        message.current = element.additionalInformation;
                                        openAdditionalInfo();
                                    }
                                }}
                            >
                                {t('Additional Information: ')}
                                {(element.additionalInformation && element.additionalInformation !== '') ? element.additionalInformation : '-'}
                            </Grid>
                        </Grid>
                    </Paper>
                ))
            );
        } else {
            return (
                <>
                    <InformationBox type="info">
                        {t('Studies have not been done.')}
                    </InformationBox>
                </>
            );
        }
    };

    useEffect(() => {
        if (props.onHideSubmit) props.onHideSubmit(true);

        if (props.onCustomButtons) {
            const buttons = [];

            if (canEditUrinalysis) {
                buttons.push(
                    <AgyleButton
                        colour="primary"
                        disabled={!canEditUrinalysis}
                        startIcon={<SettingsIcon/>}
                        onClick={openSettings}
                    >
                        {t('Configure tests')}
                    </AgyleButton>,
                );
            }

            if (canAddUrinalysis) {
                buttons.push(
                    <AgyleButton
                        colour="primary"
                        disabled={!canAddUrinalysis || props.disableSave}
                        startIcon={<BiotechIcon/>}
                        onClick={openNewTest}
                    >
                        {t('Add new entry')}
                    </AgyleButton>,
                );
            }
            props.onCustomButtons(buttons);
        }
    }, [
        patientAddData,
        canViewUrinalysis,
        canAddUrinalysis,
        canEditUrinalysis,
        list,
        currentAtt,
        isNewTestOpen,
        isSettingsOpen,
        openConfirm,
        cancelID,
        message,
        isAdditionalInfoOpen,
        props.patient,
        props.disableSave,
    ]);

    return (
        <>
            <Grid container direction="row" sx={{pt: 1}}>
                <Grid container direction="row" spacing={2} style={{fontWeight: 'bold'}}
                      className={classes.testEntry} sx={{ml: 0}}>
                    <Grid item xs={12} style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <AgyleBlockSwitch
                            onChange={e => setCurrentAtt(e)}
                            value={currentAtt}
                        />

                        <Typography variant="body1">
                            {t('Show only current attendance')}
                        </Typography>
                    </Grid>

                    <Grid item xs={3}>
                        {t('Test Strip Name')}
                    </Grid>

                    <Grid item xs={4}>
                        {t('User')}
                    </Grid>

                    <Grid item xs={3}>
                        {t('Date')}
                    </Grid>

                    <Grid item xs={1}>
                        {t('Beta-HCG')}
                    </Grid>

                    <Grid item xs={1}>
                        {t('Cancel')}
                    </Grid>

                </Grid>
                {renderLists()}
            </Grid>

            {isNewTestOpen &&
                <UrinalysisTestNew
                    patient={props.patient}
                    open={isNewTestOpen}
                    onClose={closeNewTest}
                />
            }

            {isSettingsOpen &&
                <UrinalysisSettings
                    open={isSettingsOpen}
                    onClose={closeSettings}
                />
            }

            <MessageBox
                onClose={() => setOpenConfirm(false)}
                title={t('Confirmation')}
                message={t('Are you sure you want to cancel this entry?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={cancelEntry}
                open={openConfirm}
            />

            <AgyleDialog
                open={isAdditionalInfoOpen}
                onClose={closeAdditionalInfo}
                width="sm"
                fullWidth
                noPadding
                disableSave
                closeButtonOnHeader
                title={t('Additional Information')}
            >
                <DialogContent dividers>
                    <Grid item xs={12}>
                        <AgyleTextField
                            style={{width: '100%'}}
                            label={t('Additional Information')}
                            multiline
                            value={message.current}
                            variant="outlined"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                readOnly: true,
                            }}
                            color="tertiary"
                            readOnly
                        />
                    </Grid>
                </DialogContent>
            </AgyleDialog>
        </>
    );
};

export default Urinalysis;